import { Router, watchBusy } from "@frui.ts/screens";
import { attachAutomaticValidator, validate } from "@frui.ts/validation";
import { bound } from "@frui.ts/helpers";
import { Modules } from "config";
import type { interfaces } from "inversify";
import { computed, observable } from "mobx";
import { endOfDay, startOfDay } from "date-fns";
import type BlacklistedEmailAddress from "entities/blacklistedEmailAddress";
import { BlacklistedEmailAddressEditValidationRules } from "entities/blacklistedEmailAddressRequest";
import EmailBlacklistRepository from "repositories/emailBlacklistRepository";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import SecurityService from "services/securityService";
import EditableDetailViewModel from "../../editableDetailViewModel";

@Router.registerRoute({ name: Router.Self, route: ":emailBlacklistId" })
export default class EmailBlacklistDetailViewModel extends EditableDetailViewModel<BlacklistedEmailAddress> {
  readonly modelName = "emailBlacklist";

  @observable
  deactivateImmediately: boolean = false;

  @observable
  forceReadOnly: boolean = false;

  @computed
  get isEditable() {
    if (this.forceReadOnly) {
      return false;
    }
    if (!this.item) {
      return false;
    }
    // Check both global write permission and item-level permittedActions from BE
    const hasWritePermission = this.securityService.allowTo("charges.emailblacklist", "write");
    return hasWritePermission && (this.item.permittedActions?.blacklistedEmailAddressChange?.isAllowed ?? false);
  }

  @computed
  get isValidSinceInFuture() {
    if (!this.item?.validSince) {
      return false;
    }
    return startOfDay(this.item.validSince) > startOfDay(new Date());
  }

  constructor(
    protected itemId: string,
    protected repository: EmailBlacklistRepository,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected notificationService: NotificationService,
    protected securityService: SecurityService
  ) {
    super(itemId);
  }

  async loadDetail(): Promise<BlacklistedEmailAddress> {
    const item = await this.repository.get(this.itemId);
    attachAutomaticValidator(item, BlacklistedEmailAddressEditValidationRules);
    return item;
  }

  @bound
  @watchBusy
  async save() {
    if (!validate(this.item)) {
      return;
    }

    // Zkontroluj, jestli formulář obsahuje neplatné datum (DatePickerBox má is-invalid class)
    const hasInvalidDate = document.querySelector(".modal .is-invalid") !== null;
    if (hasInvalidDate) {
      return;
    }

    if (this.deactivateImmediately) {
      // Pokud validSince je v budoucnu, nelze ukončit ihned
      if (this.isValidSinceInFuture) {
        return;
      }
      this.item.validUntil = new Date();
    } else if (this.item.validUntil) {
      this.item.validUntil = endOfDay(this.item.validUntil);
    }

    await super.save();
  }

  async updateData(): Promise<BlacklistedEmailAddress> {
    if (this.deactivateImmediately) {
      await this.repository.update(this.item.id, {
        description: this.item.description,
        hasImmediateEndOfValidity: true,
        validUntilActionType: "CHANGE",
      });
    } else if (this.item.validUntil !== undefined) {
      await this.repository.update(this.item.id, {
        description: this.item.description,
        validUntilDate: this.item.validUntil,
        validUntilActionType: "CHANGE",
      });
    } else {
      await this.repository.update(this.item.id, {
        description: this.item.description,
        validUntilActionType: "NONE",
      });
    }
    return this.item;
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.administration, `emailBlacklists.${code}`, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string, forceReadOnly = false) => {
      const vm = new EmailBlacklistDetailViewModel(
        itemId,
        container.get(EmailBlacklistRepository),
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(NotificationService),
        container.get(SecurityService)
      );
      vm.forceReadOnly = forceReadOnly;
      return vm;
    };
  }
}
